import type { APITypes, MedicalDataTypes, Dynamo } from '../../../../primary-record-types';

export const isResourceNoteEntityType = (
    entityType: Dynamo.NoteEntityType,
): entityType is Dynamo.ResourceNoteEntityType => entityType.endsWith('-resource');

export const extractResourceNoteEntityTypeResourceCategory = (
    entityType: Dynamo.ResourceNoteEntityType,
): MedicalDataTypes.PrimaryRecordResourceCategory =>
    entityType.replace('-resource', '') as MedicalDataTypes.PrimaryRecordResourceCategory;

export type AttachmentType = 'image' | 'file';

export type UserNoteDestination = {
    entityType: 'user';
};

export const isUserNoteDestination = (destination: NoteDestination): destination is UserNoteDestination =>
    destination.entityType === 'user';

export type OrganizationNoteDestination = {
    entityType: 'organization';
    organizationId: string;
};

export const isOrganizationNoteDestination = (
    destination: NoteDestination,
): destination is OrganizationNoteDestination => destination.entityType === 'organization';

export type OrganizationClientRecordNoteDestination = {
    entityType: 'organization-client-record';
    organizationId: string;
    organizationClientRecordId: string;
};

export const isOrganizationClientRecordNoteDestination = (
    destination: NoteDestination,
): destination is OrganizationClientRecordNoteDestination => destination.entityType === 'organization-client-record';

export type MedicalProfileNoteDestination = {
    entityType: 'medical-profile';
    medicalProfileId: string;
};

export const isMedicalProfileNoteDestination = (
    destination: NoteDestination,
): destination is MedicalProfileNoteDestination => destination.entityType === 'medical-profile';

export type CareTeamNoteDestination = {
    entityType: 'care-team';
    medicalProfileId: string;
};

export const isCareTeamNoteDestination = (destination: NoteDestination): destination is CareTeamNoteDestination =>
    destination.entityType === 'care-team';

export type CareTeamMemberNoteDestination = {
    entityType: 'care-team-member';
    entityId: string;
    medicalProfileId: string;
};

export const isCareTeamMemberNoteDestination = (
    destination: NoteDestination,
): destination is CareTeamMemberNoteDestination => destination.entityType === 'care-team-member';

export type CareTeamDomainNoteDestination = CareTeamNoteDestination | CareTeamMemberNoteDestination;
export const isCareTeamDomainNoteDestination = (
    destination: NoteDestination,
): destination is CareTeamDomainNoteDestination =>
    isCareTeamNoteDestination(destination) || isCareTeamMemberNoteDestination(destination);

export type ResourceCategoryNoteDestination = {
    entityType: MedicalDataTypes.PrimaryRecordResourceCategory;
    medicalProfileId: string;
};

export const isResourceCategoryNoteDestination = (
    destination: NoteDestination,
): destination is ResourceCategoryNoteDestination => {
    return (
        destination.entityType === 'allergy' ||
        destination.entityType === 'condition' ||
        destination.entityType === 'medication' ||
        destination.entityType === 'visit' ||
        destination.entityType === 'imaging' ||
        destination.entityType === 'vaccination' ||
        destination.entityType === 'procedure' ||
        destination.entityType === 'lab'
    );
};

export type ResourceNoteDestination = {
    entityType: Dynamo.ResourceNoteEntityType;
    medicalProfileId: string;
    entityId: string;
};

export const isResourceNoteDestination = (destination: NoteDestination): destination is ResourceNoteDestination => {
    return isResourceNoteEntityType(destination.entityType);
};
export type ResourceDomainNoteDestination = ResourceCategoryNoteDestination | ResourceNoteDestination;

export const isResourceDomainNoteDestination = (
    destination: NoteDestination,
): destination is ResourceDomainNoteDestination =>
    isResourceCategoryNoteDestination(destination) || isResourceNoteDestination(destination);

export type NonUserNoteDestination = Exclude<NoteDestination, UserNoteDestination>;

export type ProfileNoteDestination = Exclude<
    NoteDestination,
    UserNoteDestination | OrganizationNoteDestination | OrganizationClientRecordNoteDestination
>;

export const isNonUserNoteDestination = (destination: NoteDestination): destination is NonUserNoteDestination =>
    !isUserNoteDestination(destination);

export const isProfileNoteDestination = (destination: NoteDestination): destination is ProfileNoteDestination =>
    !!(destination as ProfileNoteDestination).medicalProfileId;

export type NoteDestination =
    | UserNoteDestination
    | MedicalProfileNoteDestination
    | CareTeamNoteDestination
    | CareTeamMemberNoteDestination
    | ResourceCategoryNoteDestination
    | ResourceNoteDestination
    | OrganizationNoteDestination
    | OrganizationClientRecordNoteDestination;

export type NoteDestinationParams = NoteDestination & {
    defaultOpenAttachmentType?: AttachmentType;
};

export const noteDetailsToDestination = (note: APITypes.notesService.NoteDetails): NoteDestination => {
    switch (note.entityType) {
        case 'user':
            return {
                entityType: 'user',
            };
        case 'medical-profile':
            return {
                entityType: 'medical-profile',
                medicalProfileId: note.entityId,
            };
        case 'care-team':
            return {
                entityType: 'care-team',
                medicalProfileId: note.entityId,
            };
        case 'care-team-member':
            return {
                entityType: 'care-team-member',
                entityId: note.entityId,
                medicalProfileId: note.medicalProfileId!,
            };
        case 'organization':
            return {
                entityType: 'organization',
                organizationId: note.entityId,
            };
        case 'organization-client-record':
            return {
                entityType: 'organization-client-record',
                organizationId: note.organizationId!,
                organizationClientRecordId: note.entityId!,
            };
        case 'condition-resource':
        case 'medication-resource':
        case 'allergy-resource':
        case 'visit-resource':
        case 'vaccination-resource':
        case 'procedure-resource':
        case 'imaging-resource':
        case 'lab-resource':
            return {
                entityType: note.entityType,
                medicalProfileId: note.medicalProfileId!,
                entityId: note.entityId,
            };
        default:
            return {
                entityType: note.entityType,
                medicalProfileId: note.medicalProfileId!,
            };
    }
};

export const resourceCategoryToResourceNoteEntityTypeV2 = (
    category: MedicalDataTypes.PrimaryRecordResourceCategory,
): Dynamo.ResourceNoteEntityType => {
    const categoryMap: Record<MedicalDataTypes.PrimaryRecordResourceCategory, Dynamo.ResourceNoteEntityType> = {
        allergy: 'allergy-resource',
        condition: 'condition-resource',
        medication: 'medication-resource',
        visit: 'visit-resource',
        vaccination: 'vaccination-resource',
        procedure: 'procedure-resource',
        imaging: 'imaging-resource',
        lab: 'lab-resource',
    };

    return categoryMap[category];
};

export const resourceNoteEntityTypeToResourceCategory = (
    resourceNoteEntityType: Dynamo.ResourceNoteEntityType,
): MedicalDataTypes.PrimaryRecordResourceCategory => {
    const categoryMap: Record<Dynamo.ResourceNoteEntityType, MedicalDataTypes.PrimaryRecordResourceCategory> = {
        'allergy-resource': 'allergy',
        'condition-resource': 'condition',
        'medication-resource': 'medication',
        'visit-resource': 'visit',
        'vaccination-resource': 'vaccination',
        'procedure-resource': 'procedure',
        'imaging-resource': 'imaging',
        'lab-resource': 'lab',
    };

    return categoryMap[resourceNoteEntityType];
};
